import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  ONBOARDING_IMAGE_MAX,
  onboardingGoalSchema,
  stepConditionSchema,
  workContextSchema,
} from '@platform/schema/onboarding';
import {
  ORDERABLE,
  REQUIRED,
  stepName,
} from '@platform/schema/onboarding-flow';
import { sceneArtUrl, sceneSizedArt } from '@platform/schema/scene-image';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { useDraft } from '../authoring/draft';
import { EditableText } from '../authoring/EditableText';
import { moved } from '../ordering/moved';

import { ArtGenerate } from './ArtGenerate';
import {
  STEP_COPY,
  STEP_DATA,
  STEP_PURPOSE,
  VALUE_NAMES,
  optionKey,
  reword,
  screenOf,
  valuesIn,
  withSamples,
} from './config';
import { CONTROLS, labelOf, type Control } from './controls';
import { ControlNote, FocusNotes, GoalLimit } from './ScreenNotes';

import type {
  FlowStep,
  OnboardingChallenge,
  OnboardingConfig,
  OnboardingCopyCatalog,
  StepArt,
  StepCondition,
} from '@platform/schema/onboarding';
import type { FlowProblem } from '@platform/schema/onboarding-flow';
import type { ReactNode } from 'react';

const CATALOG_PREVIEW = 200;
const THUMBNAIL = 96;

type Added = Extract<FlowStep, { kind: 'INFO' | 'QUESTION' }>;
type Image = Extract<StepArt, { kind: 'IMAGE' }>;

export interface StepEditorProps {
  step: FlowStep;
  config: OnboardingConfig;
  catalog: OnboardingCopyCatalog;
  problems: readonly FlowProblem[];
  challenge: OnboardingChallenge | null;
  disabled: boolean;
  onConfig: (next: OnboardingConfig) => void;
  onStep: (next: FlowStep) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

/** The selected screen: its purpose, then every field it shows, in labelled sections. */
export function StepEditor(props: StepEditorProps) {
  const { step, config, catalog, problems, disabled, onStep } = props;
  const english = (key: string) =>
    catalog.entries.find((row) => row.key === key)?.english ?? key;
  const required = step.kind === 'BUILT_IN' ? REQUIRED[step.id] : undefined;
  const usedFor = step.kind === 'BUILT_IN' ? STEP_DATA[step.id] : undefined;

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h6">{stepName(step)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {step.kind === 'BUILT_IN'
            ? STEP_PURPOSE[step.id]
            : step.kind === 'INFO'
              ? 'An information screen you added. It asks nothing.'
              : 'A question you added. Its answer stays on the learner’s device.'}
        </Typography>
      </Stack>

      {usedFor !== undefined && (
        <Section title="What its answers are used for">
          {usedFor.map((line) => (
            <Typography key={line} variant="caption" color="text.secondary">
              {line}
            </Typography>
          ))}
        </Section>
      )}

      {problems.map((problem) => (
        <Alert
          key={problem.message}
          severity={problem.level === 'block' ? 'error' : 'warning'}
        >
          {problem.message}
        </Alert>
      ))}

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={step.enabled}
              disabled={disabled || (required !== undefined && step.enabled)}
              onChange={(event) => {
                onStep({ ...step, enabled: event.target.checked });
              }}
            />
          }
          label={step.enabled ? 'Shown' : 'Turned off'}
        />
        {step.kind !== 'BUILT_IN' && (
          <>
            <Button
              size="small"
              disabled={disabled}
              onClick={props.onDuplicate}
            >
              Duplicate
            </Button>
            <Button
              size="small"
              color="error"
              disabled={disabled}
              onClick={props.onRemove}
            >
              Remove
            </Button>
          </>
        )}
      </Stack>
      {required !== undefined && (
        <Typography variant="caption" color="text.secondary">
          Required, so it cannot be turned off, removed or duplicated:{' '}
          {required}
        </Typography>
      )}

      {step.kind === 'BUILT_IN' ? (
        <Section title="Text">
          {catalog.entries
            .filter((row) => STEP_COPY[step.id].includes(screenOf(row.key)))
            .map(({ key }) => (
              <CopyField
                key={key}
                label={labelOf(key)}
                control={CONTROLS[key]}
                english={english(key)}
                value={
                  config.copy.find((row) => row.key === key)?.text ??
                  english(key)
                }
                disabled={disabled}
                onSave={(text) => {
                  props.onConfig(reword(config, key, text, english(key)));
                }}
              />
            ))}
        </Section>
      ) : (
        <AddedFields step={step} disabled={disabled} onStep={onStep} />
      )}

      {step.kind === 'BUILT_IN' && step.id in ORDERABLE && (
        <Section title="Answer order">
          <OrderEditor
            ids={
              step.order ?? [...ORDERABLE[step.id as keyof typeof ORDERABLE]]
            }
            label={(id) => english(optionKey(step.id, id))}
            disabled={disabled}
            onChange={(order) => {
              onStep({ ...step, order });
            }}
          />
        </Section>
      )}

      {step.kind === 'BUILT_IN' && step.id === 'GOAL' && (
        <Section title="How many goals">
          <GoalLimit step={step} disabled={disabled} onStep={onStep} />
        </Section>
      )}

      {step.id === 'STARTING_FOCUS' && (
        <Section title="How this screen works">
          <FocusNotes
            words={(key) =>
              config.copy.find((row) => row.key === key)?.text ?? english(key)
            }
          />
        </Section>
      )}

      {step.id === 'FIRST_CHALLENGE' && (
        <ChallengeSection challenge={props.challenge} />
      )}

      <Section title="Visual">
        <ArtEditor
          art={step.art}
          disabled={disabled}
          onChange={(art) => {
            onStep({ ...step, art });
          }}
        />
        <ArtGenerate
          stepId={step.id}
          screen={
            step.kind === 'BUILT_IN'
              ? STEP_PURPOSE[step.id]
              : `${step.title} ${step.body ?? ''}`.trim()
          }
          art={step.art}
          disabled={disabled}
          onApply={(art) => {
            onStep({ ...step, art });
          }}
        />
      </Section>

      <Section title="Who sees this screen">
        <ConditionEditor
          condition={step.showIf}
          label={(key) => english(key)}
          disabled={disabled}
          onChange={(showIf) => {
            onStep({ ...step, showIf });
          }}
        />
      </Section>
    </Stack>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack
      spacing={1.5}
      component="section"
      aria-label={title}
      sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      {children}
    </Stack>
  );
}

/**
 * One of the app's strings, reworded. Its `{{values}}` are offered as readable choices,
 * and the line under it shows the text as a learner would read it.
 */
function CopyField({
  label,
  control,
  english,
  value,
  disabled,
  onSave,
}: {
  label: string;
  control?: Control | undefined;
  english: string;
  value: string;
  disabled: boolean;
  onSave: (next: string) => void;
}) {
  const { draft, setDraft, reset } = useDraft(value);
  const values = valuesIn(english);
  const save = (text: string) => {
    const next = text.trim();
    if (next === '' || next === value) reset();
    else onSave(next);
  };
  const sample = withSamples(draft);

  return (
    <Stack spacing={0.5}>
      {control !== undefined && <ControlNote control={control} />}
      <TextField
        label={label}
        value={draft}
        size="small"
        fullWidth
        multiline
        disabled={disabled}
        helperText={
          draft === english ? undefined : `The app’s words: ${english}`
        }
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={() => {
          save(draft);
        }}
      />
      {values.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Insert value:
          </Typography>
          {values.map((name) => (
            <Button
              key={name}
              size="small"
              variant="outlined"
              disabled={disabled}
              onClick={() => {
                save(`${draft} {{${name}}}`);
              }}
            >
              {VALUE_NAMES[name] ?? name}
            </Button>
          ))}
          <Typography variant="caption" color="text.secondary">
            Reads: {sample}
          </Typography>
        </Stack>
      )}
      {draft !== english && (
        <Link
          component="button"
          variant="caption"
          sx={{ alignSelf: 'flex-start' }}
          disabled={disabled}
          onClick={() => {
            onSave(english);
          }}
        >
          Use the app’s words
        </Link>
      )}
    </Stack>
  );
}

function OrderEditor({
  ids,
  label,
  disabled,
  onChange,
}: {
  ids: readonly string[];
  label: (id: string) => string;
  disabled: boolean;
  onChange: (order: string[]) => void;
}) {
  const move = (from: number, to: number) => {
    onChange(moved(ids, from, to));
  };
  return (
    <Stack component="ol" spacing={0.5} sx={{ m: 0, pl: 3 }}>
      {ids.map((id, at) => (
        <Stack
          component="li"
          key={id}
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', display: 'list-item' }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {label(id)}
            </Typography>
            <Button
              size="small"
              aria-label={`Move ${label(id)} up`}
              disabled={disabled || at === 0}
              onClick={() => {
                move(at, at - 1);
              }}
            >
              ▲
            </Button>
            <Button
              size="small"
              aria-label={`Move ${label(id)} down`}
              disabled={disabled || at === ids.length - 1}
              onClick={() => {
                move(at, at + 1);
              }}
            >
              ▼
            </Button>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function AddedFields({
  step,
  disabled,
  onStep,
}: {
  step: Added;
  disabled: boolean;
  onStep: (next: FlowStep) => void;
}) {
  const field = (label: string, value: string, key: keyof Added) => (
    <EditableText
      key={`${key}:${value}`}
      label={label}
      value={value}
      multiline
      disabled={disabled}
      onSave={(text) => {
        onStep({ ...step, [key]: text });
      }}
    />
  );

  return (
    <>
      <Section title="Text">
        {field('Name in this list', step.name, 'name')}
        {field('Headline', step.title, 'title')}
        <EditableText
          key={`body:${step.body ?? ''}`}
          label="Subheadline (optional)"
          value={step.body ?? ''}
          multiline
          disabled={disabled}
          onSave={(text) => {
            onStep({ ...step, body: text });
          }}
        />
        {step.body !== undefined && (
          <Link
            component="button"
            variant="caption"
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => {
              onStep({ ...step, body: undefined });
            }}
          >
            Remove the subheadline
          </Link>
        )}
        {field('Primary button text', step.button, 'button')}
      </Section>
      {step.kind === 'QUESTION' && (
        <Section title="Answers">
          {step.options.map((option, at) => (
            <Stack
              key={option.id}
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center' }}
            >
              <EditableText
                key={option.label}
                label={`Answer ${String(at + 1)}`}
                value={option.label}
                disabled={disabled}
                onSave={(label) => {
                  onStep({
                    ...step,
                    options: step.options.map((one) =>
                      one.id === option.id ? { ...one, label } : one,
                    ),
                  });
                }}
              />
              <Button
                size="small"
                aria-label={`Move answer ${String(at + 1)} up`}
                disabled={disabled || at === 0}
                onClick={() => {
                  onStep({ ...step, options: moved(step.options, at, at - 1) });
                }}
              >
                ▲
              </Button>
              <Button
                size="small"
                color="error"
                disabled={disabled}
                onClick={() => {
                  onStep({
                    ...step,
                    options: step.options.filter((one) => one.id !== option.id),
                  });
                }}
              >
                Remove
              </Button>
            </Stack>
          ))}
          <Button
            size="small"
            sx={{ alignSelf: 'flex-start' }}
            disabled={disabled || step.options.length >= 12}
            onClick={() => {
              const id = `answer-${Math.random().toString(36).slice(2, 8)}`;
              onStep({
                ...step,
                options: [...step.options, { id, label: 'New answer' }],
              });
            }}
          >
            Add an answer
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={step.multiple}
                disabled={disabled}
                onChange={(event) => {
                  onStep({ ...step, multiple: event.target.checked });
                }}
              />
            }
            label="Learners can pick more than one answer"
          />
          <FormControlLabel
            control={
              <Switch
                checked={step.optional}
                disabled={disabled}
                onChange={(event) => {
                  onStep({
                    ...step,
                    optional: event.target.checked,
                    skipLabel: event.target.checked
                      ? (step.skipLabel ?? 'Skip for now')
                      : undefined,
                  });
                }}
              />
            }
            label="Learners can skip it"
          />
          {step.optional && (
            <EditableText
              key={`skip:${step.skipLabel ?? ''}`}
              label="Skip button text"
              value={step.skipLabel ?? ''}
              disabled={disabled}
              onSave={(skipLabel) => {
                onStep({ ...step, skipLabel });
              }}
            />
          )}
        </Section>
      )}
    </>
  );
}

function ArtEditor({
  art,
  disabled,
  onChange,
}: {
  art: StepArt | undefined;
  disabled: boolean;
  onChange: (art: StepArt | undefined) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [refused, setRefused] = useState<string>();
  const image = (uri: string, rest?: Partial<Image>): Image => ({
    kind: 'IMAGE',
    uri,
    size: 'medium',
    fit: 'contain',
    decorative: false,
    ...(art?.kind === 'IMAGE' ? art : {}),
    ...rest,
  });

  return (
    <Stack spacing={1.5}>
      <TextField
        select
        size="small"
        label="Illustration"
        value={art?.kind ?? 'DEFAULT'}
        disabled={disabled}
        sx={{ maxWidth: 320 }}
        onChange={(event) => {
          const kind = event.target.value;
          if (kind === 'DEFAULT') onChange(undefined);
          else if (kind === 'NONE') onChange({ kind: 'NONE' });
          else setPicking(true);
        }}
      >
        <MenuItem value="DEFAULT">The screen’s own (LEO)</MenuItem>
        <MenuItem value="NONE">No illustration</MenuItem>
        <MenuItem value="IMAGE">An image</MenuItem>
      </TextField>

      {art?.kind === 'IMAGE' && (
        <Stack direction="row" spacing={2}>
          <img
            src={art.uri}
            alt={art.description ?? ''}
            width={THUMBNAIL}
            style={{ objectFit: art.fit }}
          />
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1}>
              <TextField
                select
                size="small"
                label="Size"
                value={art.size}
                disabled={disabled}
                onChange={(event) => {
                  onChange({
                    ...art,
                    size: event.target.value as Image['size'],
                  });
                }}
              >
                <MenuItem value="small">Small</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="large">Large</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="Fit"
                value={art.fit}
                disabled={disabled}
                onChange={(event) => {
                  onChange({ ...art, fit: event.target.value as Image['fit'] });
                }}
              >
                <MenuItem value="contain">Show all of it</MenuItem>
                <MenuItem value="cover">Fill and crop</MenuItem>
              </TextField>
              <Button
                size="small"
                disabled={disabled}
                onClick={() => {
                  setPicking(true);
                }}
              >
                Replace
              </Button>
            </Stack>
            <FormControlLabel
              control={
                <Checkbox
                  checked={art.decorative}
                  disabled={disabled}
                  onChange={(event) => {
                    onChange({ ...art, decorative: event.target.checked });
                  }}
                />
              }
              label="Decorative: says nothing a learner needs"
            />
            {!art.decorative && (
              <EditableText
                key={art.description ?? ''}
                label="Image description, read aloud"
                value={art.description ?? ''}
                disabled={disabled}
                onSave={(description) => {
                  onChange({ ...art, description });
                }}
              />
            )}
          </Stack>
        </Stack>
      )}

      <Dialog
        open={picking}
        maxWidth="md"
        onClose={() => {
          setPicking(false);
        }}
      >
        <DialogTitle>Choose an image</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Button component="label" sx={{ alignSelf: 'flex-start' }}>
              Upload an image
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file === undefined) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    const uri =
                      typeof reader.result === 'string' ? reader.result : '';
                    if (uri.length > ONBOARDING_IMAGE_MAX) {
                      setRefused(
                        `That image is ${String(Math.round(file.size / 1000))} KB. Use one under ${String(Math.round((ONBOARDING_IMAGE_MAX * 3) / 4 / 1000))} KB.`,
                      );
                      return;
                    }
                    setRefused(undefined);
                    onChange(image(uri));
                    setPicking(false);
                  };
                  reader.readAsDataURL(file);
                }}
              />
            </Button>
            {refused !== undefined && <Alert severity="error">{refused}</Alert>}
            <Typography variant="subtitle2">Or one from the catalog</Typography>
            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              {sceneSizedArt.map((name) => (
                <ButtonBase
                  key={name}
                  aria-label={name}
                  onClick={() => {
                    onChange(image(sceneArtUrl(name)));
                    setPicking(false);
                  }}
                >
                  <img
                    src={sceneArtUrl(name)}
                    alt={name}
                    width={CATALOG_PREVIEW}
                  />
                </ButtonBase>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}

const CONDITION_VALUES = {
  goal: { screen: 'goal', options: onboardingGoalSchema.options },
  workContext: { screen: 'work', options: workContextSchema.options },
} as const;

function ConditionEditor({
  condition,
  label,
  disabled,
  onChange,
}: {
  condition: StepCondition | undefined;
  label: (key: string) => string;
  disabled: boolean;
  onChange: (condition: StepCondition | undefined) => void;
}) {
  const answer = condition?.answer;
  const values = answer === undefined ? undefined : CONDITION_VALUES[answer];
  const chosen: readonly string[] = condition?.anyOf ?? [];

  return (
    <Stack spacing={1}>
      <TextField
        select
        size="small"
        label="Shown to"
        value={answer ?? 'EVERYONE'}
        disabled={disabled}
        sx={{ maxWidth: 320 }}
        onChange={(event) => {
          const next = event.target.value;
          if (next === 'goal')
            onChange({ answer: 'goal', anyOf: ['CURRENT_JOB'] });
          else if (next === 'workContext')
            onChange({ answer: 'workContext', anyOf: ['MANAGER'] });
          else onChange(undefined);
        }}
      >
        <MenuItem value="EVERYONE">Every learner</MenuItem>
        <MenuItem value="goal">Learners with a chosen goal</MenuItem>
        <MenuItem value="workContext">Learners in a chosen role</MenuItem>
      </TextField>
      {condition !== undefined && values !== undefined && (
        <Stack>
          <Typography variant="caption" color="text.secondary">
            Shown only if the learner chose one of these.
          </Typography>
          {values.options.map((value) => (
            <FormControlLabel
              key={value}
              label={
                value === 'EXPLORING'
                  ? label('goal.explore')
                  : label(`${values.screen}.${value}`)
              }
              control={
                <Checkbox
                  checked={chosen.includes(value)}
                  disabled={disabled}
                  onChange={(event) => {
                    const anyOf = event.target.checked
                      ? [...chosen, value]
                      : chosen.filter((one) => one !== value);
                    if (anyOf.length === 0) return;
                    onChange(
                      stepConditionSchema.parse({
                        answer: condition.answer,
                        anyOf,
                      }),
                    );
                  }}
                />
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function ChallengeSection({
  challenge,
}: {
  challenge: OnboardingChallenge | null;
}) {
  return (
    <Section title="Questions">
      {challenge === null ? (
        <Alert severity="warning">
          The shared challenge is not published, so onboarding has no challenge.
        </Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary">
            {challenge.title}: {String(challenge.questions.length)} questions,
            one lesson. It counts once for XP and the daily target, whatever its
            length. Every answer gets feedback, and a wrong one does not stop
            the learner.
          </Typography>
          <Stack component="ol" spacing={0.5} sx={{ m: 0, pl: 3 }}>
            {challenge.questions.map((question) => (
              <Typography component="li" variant="body2" key={question.sceneId}>
                {question.prompt}
              </Typography>
            ))}
          </Stack>
          <Button
            component={RouterLink}
            to={`/authoring/lessons/${challenge.lessonId}`}
            variant="outlined"
            sx={{ alignSelf: 'flex-start' }}
          >
            Edit, add, remove or reorder questions
          </Button>
        </>
      )}
    </Section>
  );
}
